/**
 * Object type configurations — the snapping/alignment table, and the type →
 * config resolution that reads it.
 *
 * Split out of `objectSnapping.js` (which imports three.js) so that modules
 * with no business pulling a 3D renderer can ask the one question they need:
 * is this type mounted to a wall SURFACE? `normalObjectTypes.js` is the
 * caller that forced the split — it is deliberately importable without
 * three.js or env, and its tests rely on that.
 *
 * The table below is a verbatim move. `objectSnapping.js` imports it back and
 * re-exports `getObjectConfig`, so every existing consumer is unchanged.
 */

import {
  ELECTRICAL_DEVICES,
  wallDeviceSnapConfig,
} from '../../../utils/electricalDevices';

/**
 * Object type configurations for snapping behavior and alignment
 * Extended to include wallAlignment and verticalAlignment properties
 */
import {
  STAGING_DECOR_TYPES,
  STAGING_WALL_MOUNT_HEIGHTS,
  STAGING_WALL_MOUNT_MEASURE_FROM,
} from '@shared-lib/staging';

/**
 * Mount config for staged decor, DERIVED from the staging type registry so a
 * new prop cannot be missed. `resolveObjectConfig` is an exact-key lookup with
 * an `OBJECT_CONFIGS.default` fallback — nothing is inherited by name, so a
 * type with no entry here silently mounts as the default.
 *
 * Most props are seated by the planner on their host's top surface, so they
 * mount as 'counter', whose `getVerticalPosition` deliberately returns null
 * ("leave Y alone") rather than inventing a height. The ones that HANG, and
 * the heights they hang at, come from `STAGING_WALL_MOUNT_HEIGHTS` and what
 * those heights measure from `STAGING_WALL_MOUNT_MEASURE_FROM` — the
 * planner's own constants, not a copy. The copy that used to live here named
 * two props and the ring was added to the kit later, so every consumer of
 * these tables read a wall-hung towel ring as a counter prop.
 */
const stagingObjectConfigs = () =>
  STAGING_DECOR_TYPES.reduce((acc, type) => {
    const wallHeight = STAGING_WALL_MOUNT_HEIGHTS[type];
    acc[type] = wallHeight
      ? {
          mountType: 'wall',
          mountHeight: wallHeight,
          // What `mountHeight` describes — the prop's top for a towel bar,
          // ring or hook (the hardware line), its centre otherwise — from
          // the planner's own table, so a drag seats the prop where Stage It
          // hung it. `getVerticalPosition` reads it.
          mountMeasure: STAGING_WALL_MOUNT_MEASURE_FROM[type] || 'centre',
          requiresWall: true,
          wallOffset: 0,
          floorMounted: false,
          facesWall: false,
          wallAlignment: 'surface',
          verticalAlignment: 'center',
        }
      : {
          mountType: 'counter',
          requiresWall: false,
          wallOffset: 0,
          floorMounted: false,
          facesWall: false,
          wallAlignment: 'none',
          verticalAlignment: 'bottom',
        };
    return acc;
  }, {});

export const OBJECT_CONFIGS = {
  ...stagingObjectConfigs(),
  toilet: {
    wallOffset: 0.0254, // 1 inch in meters
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Toilet faces away from wall
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  sink: {
    wallOffset: 0, // Flush with wall
    floorMounted: true, // Changed to true for vanity/sink combos
    requiresWall: true,
    facesWall: false, // Sink faces away from wall (toward user)
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  vanity: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Vanity faces away from wall (toward user)
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  bathtub: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Bathtub faces away from wall (toward user)
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  'tub-support-box': {
    // Surround for undermount/drop-in tubs. Drag math treats the support-box
    // as the canonical body of the tub group, so it needs the same wall-snap
    // config as the bare bathtub.
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  shower: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Shower faces away from wall (door faces room)
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  mirror: {
    wallOffset: 0.002, // 2mm mounting hardware gap (matches MIRROR_MOUNTING_GAP)
    floorMounted: false,
    mountHeight: 1.6, // 1.6m above floor (eye level)
    requiresWall: true,
    facesWall: false, // Mirror reflective surface faces away from wall (toward user)
    wallAlignment: 'surface',
    verticalAlignment: 'center',
    mountType: 'wall',
  },
  'wall-light': {
    wallOffset: 0, // Flush with wall (the object's own depth will provide spacing)
    floorMounted: false,
    mountHeight: 1.68, // 5'6" above floor
    requiresWall: true,
    facesWall: false, // Wall lights face away from wall (into the room)
    wallAlignment: 'surface',
    verticalAlignment: 'center',
    mountType: 'wall',
  },
  'overhead-wall-light': {
    wallOffset: 0, // Flush with wall
    floorMounted: false,
    mountHeight: 2.1, // 7 feet above floor
    requiresWall: true,
    facesWall: false, // Overhead lights face away from wall (into the room)
    wallAlignment: 'surface',
    verticalAlignment: 'center',
    mountType: 'wall',
  },
  'ceiling-light': {
    wallOffset: 0,
    floorMounted: false,
    mountHeight: 2.44, // 8 feet (ceiling height)
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'top',
    mountType: 'ceiling',
  },
  table: {
    wallOffset: 0.1, // 10cm from wall if near one
    floorMounted: true,
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  chair: {
    wallOffset: 0.05,
    floorMounted: true,
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  bed: {
    wallOffset: 0.05,
    floorMounted: true,
    requiresWall: false,
    facesWall: false, // Bed faces away from wall (headboard against wall, foot toward room)
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  sofa: {
    wallOffset: 0.05,
    floorMounted: true,
    requiresWall: false,
    facesWall: false, // Sofa back to wall, faces room
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  // Kitchen appliances
  refrigerator: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Refrigerator door faces away from wall
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  fridge: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  // A wall oven is built INTO cabinetry — usually a full-height column,
  // sometimes a lower cabinet — so it is neither floor-standing nor fixed to
  // one height. Same shape as the vent hood below: wall-aligned so it stays
  // against the run, and deliberately NO `mountHeight`. The floor-standing
  // `oven` entry is a DIFFERENT appliance (a range oven) and is left alone.
  //
  // NOTE: `floorMounted` here is INERT — its only reader was `snapObjectToWall`,
  // which was dead and has been removed. The live vertical rule is
  // `objectMounting.getVerticalPosition`, keyed on `mountType`, and this type
  // has no row in that table yet. What this entry really buys today is
  // `wallAlignment: 'surface'` (rotation is withheld, and `shouldSnapToWall`
  // sees `requiresWall`).
  //
  // Speculative but correct: dev holds zero `wall-oven` objects today (12
  // scanned `oven`, 7 `oven-surround`), and `appliances.js` still excludes
  // `wallOven` from cabinet-run ownership pending its tall-tower placement.
  // This is here so the first one to arrive behaves.
  'wall-oven': {
    wallOffset: 0,
    floorMounted: false,
    requiresWall: true,
    facesWall: false, // The oven door faces into the room
    wallAlignment: 'surface',
    verticalAlignment: 'custom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  // A vent hood hangs ABOVE the range, on the wall — or as a floating chimney
  // over a peninsula, at a range of heights depending on the range below it
  // and the ceiling — so it takes no `mountHeight`.
  //
  // NOTE: `floorMounted` here is INERT (see the wall oven above). What this
  // entry buys is `wallAlignment: 'surface'` + `requiresWall`, which is what
  // `shouldSnapToWall` and the rotation gate read. The vertical rule lives in
  // `objectMounting` and has no row for this type yet.
  'vent-hood': {
    wallOffset: 0,
    floorMounted: false,
    // NOT wall-required: `buildFloatingVentHood` exists precisely for a
    // range on a peninsula or island, 'where there's no real wall behind
    // the hood' — it rises to the ceiling on its own chimney. Forcing a
    // wall would strand that variant. `wallAlignment: 'surface'` still
    // orients the common wall-mounted case.
    requiresWall: false,
    facesWall: false, // The hood's face looks into the room, like the range
    wallAlignment: 'surface',
    verticalAlignment: 'custom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  // `range` is the canonical name for this appliance; `stove` is an alias
  // (see TYPE_SYNONYMS). Every writer here emits `range` — kitchen
  // post-processing, the layout generator, the manual run engine — and the run
  // engine already normalises the other way round in `appliances.js`
  // (`SCENE_TYPE = { stove: 'range' }`). This table used to carry `stove` and
  // no `range`, so the 43 ranges in dev fell through to `default`: 2" off the
  // wall, not wall-required, and handed a rotation editor a stove is denied.
  range: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Range controls face away from wall
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  oven: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Oven door faces away from wall
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  dishwasher: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Dishwasher door faces away from wall
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  // Laundry appliances
  washerDryer: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  washer: {
    wallOffset: 0.05,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Washer door/controls face away from wall
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  dryer: {
    wallOffset: 0.05,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Dryer door/controls face away from wall
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  // Storage
  storage: {
    wallOffset: 0.05,
    floorMounted: true,
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  cabinet: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: false,
    facesWall: false, // Cabinet doors face away from wall
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  'lower-cabinet': {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true, // Lower cabinets are typically against walls
    facesWall: false, // Cabinet doors face away from wall
    wallAlignment: 'surface', // Use 'surface' (flush is achieved via wallOffset: 0)
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  'upper-cabinet': {
    wallOffset: 0,
    floorMounted: false,
    mountHeight: 1.8, // Center Y: 56" base (1.42m) + 15" half-height (0.38m) = 1.80m
    requiresWall: true, // Upper cabinets must be on walls
    facesWall: false, // Cabinet doors face away from wall
    wallAlignment: 'surface', // Mounted on wall surface
    verticalAlignment: 'custom', // Custom height above floor
    mountType: 'wall',
  },
  fireplace: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Fireplace opening faces into room
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  'storage-furniture': {
    wallOffset: 0.1, // Small gap from wall for movable furniture
    floorMounted: true,
    requiresWall: false, // Can be placed anywhere
    facesWall: false,
    wallAlignment: 'adjacent',
    verticalAlignment: 'bottom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  // Architectural elements
  door: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'center',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  window: {
    wallOffset: 0,
    floorMounted: false,
    mountHeight: 1.2,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'center',
    verticalAlignment: 'center',
    mountType: 'wall',
  },
  opening: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'center',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  wall: {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    // Resolved to the mounting table's default before the merge.
    mountType: 'floor',
  },
  // Shower variants
  'shower-door': {
    wallOffset: 0,
    floorMounted: true,
    requiresWall: true,
    facesWall: false, // Shower door faces away from wall (into room)
    wallAlignment: 'surface',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
  'shower-controls': {
    wallOffset: 0,
    floorMounted: false,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'surface',
    verticalAlignment: 'center',
    mountType: 'wall',
    mountHeight: 1,
  },
  'shower-stem': {
    wallOffset: 0,
    floorMounted: false,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'surface',
    verticalAlignment: 'center',
    mountType: 'wall',
    mountHeight: 2,
  },
  // Held by a COUNTERTOP, not by the floor or a wall. Both routinely sit in an
  // island, so neither requires a wall; and both are seated on the real slab by
  // kitchen post-processing, so `mountType: 'counter'` tells the vertical rule
  // to leave their Y alone. Without an entry they fell to `default` -> 'floor',
  // and any dimension edit dropped them to the floor.
  cooktop: {
    wallOffset: 0,
    floorMounted: false,
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'custom',
    mountType: 'counter',
  },
  'kitchen-sink': {
    wallOffset: 0,
    floorMounted: false,
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'custom',
    mountType: 'counter',
  },
  // Folded in from the former `objectMounting.OBJECT_MOUNT_CONFIG`, which
  // was the only table that knew these types. The alignment fields are the
  // ones they used to inherit from `default`.
  'bathroom-tub-faucet': {
    mountType: 'floor',
    requiresWall: false,
    wallOffset: 0.05, // was inherited from `default`; the old
    // `OBJECT_MOUNT_CONFIG` value here was never read
    floorMounted: true,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
  },
  showerhead: {
    mountType: 'wall',
    mountHeight: 2,
    requiresWall: true,
    wallOffset: 0.05, // was inherited from `default`; the old
    // `OBJECT_MOUNT_CONFIG` value here was never read
    floorMounted: true,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
  },
  'ceiling-fan': {
    mountType: 'ceiling',
    mountHeight: 2.44,
    requiresWall: false,
    wallOffset: 0.05, // was inherited from `default`; the old
    // `OBJECT_MOUNT_CONFIG` value here was never read
    floorMounted: true,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
  },
  'dorm-floor-plant': {
    mountType: 'floor',
    requiresWall: false,
    wallOffset: 0.05, // was inherited from `default`; the old
    // `OBJECT_MOUNT_CONFIG` value here was never read
    floorMounted: true,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
  },
  'dorm-desk-plant': {
    mountType: 'floor',
    requiresWall: false,
    wallOffset: 0.05, // was inherited from `default`; the old
    // `OBJECT_MOUNT_CONFIG` value here was never read
    floorMounted: true,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
  },
  'dorm-hanging-planter': {
    mountType: 'ceiling',
    mountHeight: 2.13,
    requiresWall: false,
    wallOffset: 0.05, // was inherited from `default`; the old
    // `OBJECT_MOUNT_CONFIG` value here was never read
    floorMounted: true,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
  },
  default: {
    wallOffset: 0.05,
    floorMounted: true,
    requiresWall: false,
    facesWall: false,
    wallAlignment: 'none',
    verticalAlignment: 'bottom',
    mountType: 'floor',
  },
};

// Wall-mounted electrical devices (switches, outlets) — derived from the one
// catalog so the snap height and wall offset cannot drift from the device size.
for (const type of Object.keys(ELECTRICAL_DEVICES)) {
  const config = wallDeviceSnapConfig(type);
  if (config) OBJECT_CONFIGS[type] = config;
}

/**
 * Resolve a type (with an optional category fallback) to its config.
 *
 * Moved verbatim out of `getObjectConfig`'s base lookup, which now calls this
 * and then applies its own toilet wall-hung override on top.
 */
/**
 * Type synonyms — two names for ONE physical thing.
 *
 * Distinct from the category fallbacks in `getObjectDefaults`, which map a
 * category name onto a config key. These are the same object typed two ways by
 * different producers, so every lookup must fold them before it reads the
 * table; otherwise the two names drift apart in whichever paths forget.
 *
 * Deliberately narrow: a `cooktop` is NOT a range (no oven under it) and a
 * `wood-stove` is not either. Only add a pair here when the two names denote
 * the same object.
 */
export const TYPE_SYNONYMS = { stove: 'range', 'range-hood': 'vent-hood' };

/** The canonical name for a type, folding any synonym. */
export const canonicalObjectType = (objectType) =>
  TYPE_SYNONYMS[objectType] || objectType;

export const resolveObjectConfig = (objectType, category) => {
  // Check direct type match, on the canonical name
  let base = OBJECT_CONFIGS[canonicalObjectType(objectType)];
  if (!base) {
    // Check category match
    const categoryName =
      typeof category === 'string'
        ? category
        : category && typeof category === 'object'
          ? Object.keys(category)[0]
          : null;
    base =
      (categoryName && OBJECT_CONFIGS[categoryName]) || OBJECT_CONFIGS.default;
  }
  return base;
};

/**
 * Is this object type mounted flat against a wall SURFACE?
 *
 * `wallAlignment: 'surface'` is what makes a horizontal move run the wall-snap
 * branch, which recomputes the object's rotation from the wall it lands on.
 * Anything that offers such a type a rotation control is offering one the next
 * move silently undoes — the angle belongs to the wall.
 */
export const isWallSurfaceMounted = (objectType, category) =>
  resolveObjectConfig(objectType, category)?.wallAlignment === 'surface';
